import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Button, Input, Select, Space, Table, Tooltip, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Box } from 'atoms';
import StatusChip from 'componentsv2/common/StatusChip';
import { useWarehousePermissions } from 'hooks';
import { numberWithCommas } from 'utils/NumberCommaSeprator';
import {
	createWarehouseMonthlyCostEntryV2,
	createWarehouseMonthlyCostHeadV2,
	createWarehouseRevenueAdjustmentV2,
	deleteWarehouseMonthlyCostEntryV2,
	updateWarehouseMonthlyCostEntryV2,
} from 'store/warehousingSliceV2';
import {
	formatIstDateTime,
	ADD_COST_HEAD_OPTION,
	COST_HEAD_OPTIONS,
	MODEL_COST_PLUS,
	MONTHLY_STATUS_PENDING,
} from '../warehousing.constants';
import MonthlyCostEditModal from './MonthlyCostEditModal';
import {
	AddRowNote,
	CostHeadAddRow,
	EmptyState,
	NumCell,
	Panel,
	PanelHeader,
	PanelSubtitle,
	PanelTitle,
	MetricPill,
	PanelHeaderMetrics,
	QuickAddField,
	WideGutterTable,
} from './warehousingStyles';

const money = (value) =>
	value === null || value === undefined ? '—' : `₹ ${numberWithCommas(Math.round(value))}`;

const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_FORMAT = 'YYYY-MM';

const ADJUSTMENT_TYPE_OPTIONS = [
	{ value: 'addition', label: 'Addition' },
	{ value: 'deduction', label: 'Deduction' },
];

/** The one reason "Add & Save" can't run yet, or null when the staged head is valid. */
const validateNewMonthlyHead = ({ name, addingCustom, value, costToCustomer, isCostPlus }) => {
	if (!name) return addingCustom ? 'Enter a name for the new cost head.' : 'Select a cost head.';
	if (value === '' || Number.isNaN(Number(value))) return 'Enter a value.';
	if (isCostPlus && costToCustomer !== '' && Number(costToCustomer) < Number(value)) {
		return 'Cost to Customer cannot be less than Value.';
	}
	return null;
};

// Every column but the head is given an explicit width. Without them antd shares the
// table evenly, which put the right-aligned Value hard against the Added On timestamp.
// The head column takes the slack instead, so the metadata columns keep their gutters.
const revenueAdjustmentColumns = [
	{ title: 'Revenue Head', dataIndex: 'revenue_head', key: 'revenue_head', ellipsis: true },
	{
		title: 'Type',
		dataIndex: 'adjustment_type',
		key: 'adjustment_type',
		width: 130,
		render: (value) =>
			value === 'addition' ? (
				<StatusChip tone="success" label="Addition" />
			) : (
				<StatusChip tone="danger" label="Deduction" />
			),
	},
	{
		title: 'Value',
		dataIndex: 'value',
		key: 'value',
		width: 130,
		align: 'right',
		render: (value) => <NumCell>{money(value)}</NumCell>,
	},
	{
		title: 'Added On',
		dataIndex: 'added_at',
		key: 'added_at',
		width: 190,
		render: (value) => formatIstDateTime(value),
	},
	{
		title: 'Added By',
		dataIndex: 'added_by',
		key: 'added_by',
		width: 160,
		ellipsis: true,
		render: (v) => v || '—',
	},
];

/**
 * Monthly tab — month-end variable cost heads for the selected period.
 *
 * A pending head takes its first value inline. A head that already has one is edited
 * or removed through MonthlyCostEditModal, which collects the `reason` the server
 * requires; both are limited to the cost-editor roles. A pending row has no
 * entry server-side yet — its `id` is null and the value shown is last month's figure
 * carried forward — so it can only be created, never edited.
 */
export default function MonthlyTab({ projectId, periodMonth, figures, loading, onDataChanged }) {
	const dispatch = useDispatch();
	const {
		canAddMonthlyCost,
		canAddMonthlyCostHead,
		canAddRevenueAdjustment,
		canEditMonthlyCost,
		canDeleteMonthlyCost,
	} = useWarehousePermissions();

	const [saving, setSaving] = useState(false);
	const [editTarget, setEditTarget] = useState(null); // { mode, record }
	const [savingEdit, setSavingEdit] = useState(false);
	const [drafts, setDrafts] = useState({});
	const [draftsCostToCustomer, setDraftsCostToCustomer] = useState({});
	const [selectedHead, setSelectedHead] = useState(undefined);
	const [customHead, setCustomHead] = useState('');
	const [newValue, setNewValue] = useState('');
	const [newCostToCustomer, setNewCostToCustomer] = useState('');

	const isCostPlus = figures?.revenue_model?.model_type === MODEL_COST_PLUS;

	const [adjustmentHead, setAdjustmentHead] = useState('');
	const [adjustmentType, setAdjustmentType] = useState('addition');
	const [adjustmentValue, setAdjustmentValue] = useState('');
	const [savingAdjustment, setSavingAdjustment] = useState(false);

	const addingCustom = selectedHead === ADD_COST_HEAD_OPTION;
	const costFrozen = Boolean(figures?.cost_frozen);
	const revenueFrozen = Boolean(figures?.revenue_frozen);
	// Freeze is scoped to the ONE month selected above, never the whole project.
	const monthLabel = periodMonth.format('MMMM YYYY');
	const costFrozenTooltip = costFrozen
		? `Cost is frozen for ${monthLabel} — no further entries`
		: undefined;

	const rows = useMemo(() => figures?.monthly_costs || [], [figures]);
	const revenueAdjustments = useMemo(() => figures?.revenue_adjustments || [], [figures]);

	/**
	 * Heads awaiting a value this period, then the standard shortlist minus anything
	 * already in the catalog, and finally the "+ Add Cost Head" escape hatch.
	 */
	const headOptions = useMemo(() => {
		const existing = new Set(rows.map((r) => (r.cost_head_name || '').toLowerCase()));

		const pending = rows
			.filter((r) => r.status === MONTHLY_STATUS_PENDING)
			.map((r) => ({ value: r.cost_head_name, label: r.cost_head_name }));

		const shortlist = COST_HEAD_OPTIONS.filter((name) => !existing.has(name.toLowerCase())).map(
			(name) => ({ value: name, label: name })
		);

		return [
			...pending,
			...shortlist,
			{ value: ADD_COST_HEAD_OPTION, label: '+ Add Cost Head' },
		];
	}, [rows]);

	const enteredTotal = useMemo(
		() => rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0),
		[rows]
	);
	const lastMonthTotal = useMemo(
		() => rows.reduce((sum, row) => sum + (Number(row.last_month_value) || 0), 0),
		[rows]
	);
	// Cost Plus only. Rows with no value yet (pending) or no cost_to_customer split
	// contribute their Value only, i.e. 0 Generated Revenue — same convention the
	// backend applies when aggregating MTD Total Cost to Customer.
	const enteredCostToCustomerTotal = useMemo(
		() =>
			rows.reduce((sum, row) => {
				if (row.status === MONTHLY_STATUS_PENDING) return sum;
				const ctcu =
					row.cost_to_customer === null || row.cost_to_customer === undefined
						? Number(row.value) || 0
						: Number(row.cost_to_customer);
				return sum + ctcu;
			}, 0),
		[rows]
	);
	const generatedRevenueTotal = enteredCostToCustomerTotal - enteredTotal;

	const saveHead = async (costHeadId, rawValue, rawCostToCustomer) => {
		if (rawValue === '' || rawValue === undefined || rawValue === null) return;
		if (
			isCostPlus &&
			rawCostToCustomer !== '' &&
			rawCostToCustomer !== undefined &&
			Number(rawCostToCustomer) < Number(rawValue)
		) {
			message.error('Cost to Customer cannot be less than Value.');
			return;
		}

		setSaving(true);
		try {
			await dispatch(
				createWarehouseMonthlyCostEntryV2({
					req_data: {
						project_id: projectId,
						cost_head_id: costHeadId,
						period_month: periodMonth.format(DATE_FORMAT),
						value: Number(rawValue),
						...(isCostPlus &&
						rawCostToCustomer !== '' &&
						rawCostToCustomer !== undefined
							? { cost_to_customer: Number(rawCostToCustomer) }
							: {}),
					},
				})
			);
			setDrafts((prev) => ({ ...prev, [costHeadId]: undefined }));
			setDraftsCostToCustomer((prev) => ({ ...prev, [costHeadId]: undefined }));
			onDataChanged();
		} catch (error) {
			// Errors surface through the API interceptor
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Apply an edit or a delete from the modal. Both carry the mandatory `reason`, and
	 * the entry is addressed by its own `id` — not by cost head — since a head can hold
	 * one entry per month and the id is what the server updates.
	 */
	const handleEditSubmit = async (values) => {
		const record = editTarget?.record;
		if (!record?.id) return;

		const isDelete = editTarget.mode === 'delete';
		setSavingEdit(true);
		try {
			const action = isDelete
				? deleteWarehouseMonthlyCostEntryV2
				: updateWarehouseMonthlyCostEntryV2;
			await dispatch(
				action({ req_data: { project_id: projectId, id: record.id, ...values } })
			);
			setEditTarget(null);
			onDataChanged();
		} catch (error) {
			// Errors surface through the API interceptor
		} finally {
			setSavingEdit(false);
		}
	};

	/**
	 * Add & Save. Picking an existing pending head posts a value straight against it;
	 * a name that is not in the catalog is created first, then the value is posted
	 * against the id the backend hands back.
	 */
	const handleAddAndSave = async () => {
		const name = (addingCustom ? customHead : selectedHead || '').trim();
		const error = validateNewMonthlyHead({
			name,
			addingCustom,
			value: newValue,
			costToCustomer: newCostToCustomer,
			isCostPlus,
		});
		if (error) {
			message.error(error);
			return;
		}

		const existing = rows.find(
			(r) => (r.cost_head_name || '').toLowerCase() === name.toLowerCase()
		);
		if (existing && existing.status !== MONTHLY_STATUS_PENDING) {
			message.error(`"${existing.cost_head_name}" already has a value for this period.`);
			return;
		}

		setSaving(true);
		try {
			// A head from the shortlist does not exist server-side until it is used, so
			// anything not already in the catalog is created first.
			let costHeadId = existing?.cost_head_id;

			if (!costHeadId) {
				const created = await dispatch(
					createWarehouseMonthlyCostHeadV2({
						req_data: {
							project_id: projectId,
							cost_head_name: name,
							// The month the head is being added for — the one selected on
							// this page, not necessarily the current one.
							period_month: periodMonth.format(MONTH_FORMAT),
						},
					})
				);
				costHeadId = created?.cost_head_id;
				if (!costHeadId) throw new Error('Cost head was not created');
			}

			await dispatch(
				createWarehouseMonthlyCostEntryV2({
					req_data: {
						project_id: projectId,
						cost_head_id: costHeadId,
						period_month: periodMonth.format(DATE_FORMAT),
						value: Number(newValue),
						...(isCostPlus && newCostToCustomer !== ''
							? { cost_to_customer: Number(newCostToCustomer) }
							: {}),
					},
				})
			);

			setSelectedHead(undefined);
			setCustomHead('');
			setNewCostToCustomer('');
			setNewValue('');
			onDataChanged();
		} catch (saveError) {
			// Errors surface through the API interceptor
		} finally {
			setSaving(false);
		}
	};

	/** Add a provisional "Additional Revenue Head" adjustment, then refetch the dashboard. */
	const handleAddAdjustment = async () => {
		const head = adjustmentHead.trim();
		if (!head) {
			message.error('Enter a revenue head name.');
			return;
		}
		if (
			adjustmentValue === '' ||
			Number.isNaN(Number(adjustmentValue)) ||
			Number(adjustmentValue) < 0
		) {
			message.error('Enter a valid value.');
			return;
		}

		setSavingAdjustment(true);
		try {
			await dispatch(
				createWarehouseRevenueAdjustmentV2({
					req_data: {
						project_id: projectId,
						period_month: periodMonth.format(DATE_FORMAT),
						revenue_head: head,
						adjustment_type: adjustmentType,
						value: Number(adjustmentValue),
					},
				})
			);
			setAdjustmentHead('');
			setAdjustmentType('addition');
			setAdjustmentValue('');
			onDataChanged();
		} catch (error) {
			// Errors surface through the API interceptor
		} finally {
			setSavingAdjustment(false);
		}
	};

	const renderValueCell = (value, record) => {
		const isPending = record.status === MONTHLY_STATUS_PENDING;
		if (!isPending) return <NumCell>{money(value)}</NumCell>;
		if (!canAddMonthlyCost) return <NumCell>—</NumCell>;

		const lastMonth = record.last_month_value;
		return (
			<Tooltip title={costFrozenTooltip}>
				<Input
					type="number"
					min={0}
					size="small"
					disabled={costFrozen}
					placeholder={
						lastMonth === null || lastMonth === undefined ? '0' : String(lastMonth)
					}
					aria-label={`Value for ${record.cost_head_name}`}
					value={drafts[record.cost_head_id] ?? ''}
					onChange={(e) =>
						setDrafts((prev) => ({ ...prev, [record.cost_head_id]: e.target.value }))
					}
					onPressEnter={() =>
						saveHead(
							record.cost_head_id,
							drafts[record.cost_head_id],
							draftsCostToCustomer[record.cost_head_id]
						)
					}
				/>
			</Tooltip>
		);
	};

	// Cost Plus only. A pending row takes its Cost to Customer inline alongside Value;
	// an added row shows what was saved, read-only (corrected via MonthlyCostEditModal).
	const renderCostToCustomerCell = (value, record) => {
		const isPending = record.status === MONTHLY_STATUS_PENDING;
		if (!isPending)
			return <NumCell>{value === null || value === undefined ? '—' : money(value)}</NumCell>;
		if (!canAddMonthlyCost) return <NumCell>—</NumCell>;

		return (
			<Tooltip title={costFrozenTooltip}>
				<Input
					type="number"
					min={0}
					size="small"
					disabled={costFrozen}
					placeholder="Same as Value"
					aria-label={`Cost to Customer for ${record.cost_head_name}`}
					value={draftsCostToCustomer[record.cost_head_id] ?? ''}
					onChange={(e) =>
						setDraftsCostToCustomer((prev) => ({
							...prev,
							[record.cost_head_id]: e.target.value,
						}))
					}
					onPressEnter={() =>
						saveHead(
							record.cost_head_id,
							drafts[record.cost_head_id],
							draftsCostToCustomer[record.cost_head_id]
						)
					}
				/>
			</Tooltip>
		);
	};

	const renderGeneratedRevenueCell = (_, record) =>
		record.generated_revenue === null || record.generated_revenue === undefined ? (
			<NumCell>—</NumCell>
		) : (
			<NumCell>{money(record.generated_revenue)}</NumCell>
		);

	// Pending rows can only be created; a row that already has a value is corrected or
	// removed, and only by the cost-editor roles. A pending row's `id` is null server-side,
	// so there is nothing for an edit to address even if the role allowed it.
	const showActionsColumn = canAddMonthlyCost || canEditMonthlyCost || canDeleteMonthlyCost;

	const renderActions = (_, record) => {
		if (record.status === MONTHLY_STATUS_PENDING) {
			if (!canAddMonthlyCost) return null;
			return (
				<Tooltip title={costFrozenTooltip}>
					<Button
						size="small"
						type="primary"
						icon={<PlusOutlined />}
						loading={saving}
						disabled={costFrozen || !drafts[record.cost_head_id]}
						onClick={() =>
							saveHead(
								record.cost_head_id,
								drafts[record.cost_head_id],
								draftsCostToCustomer[record.cost_head_id]
							)
						}
					>
						Save
					</Button>
				</Tooltip>
			);
		}
		if ((!canEditMonthlyCost && !canDeleteMonthlyCost) || !record.id) return null;
		return (
			<Tooltip title={costFrozenTooltip}>
				<Space size={4}>
					{canEditMonthlyCost && (
						<Button
							size="small"
							icon={<EditOutlined />}
							disabled={costFrozen}
							aria-label={`Edit ${record.cost_head_name}`}
							onClick={() => setEditTarget({ mode: 'edit', record })}
						/>
					)}
					{canDeleteMonthlyCost && (
						<Button
							size="small"
							danger
							icon={<DeleteOutlined />}
							disabled={costFrozen}
							aria-label={`Delete ${record.cost_head_name}`}
							onClick={() => setEditTarget({ mode: 'delete', record })}
						/>
					)}
				</Space>
			</Tooltip>
		);
	};

	const columns = [
		{
			title: 'Cost Head',
			dataIndex: 'cost_head_name',
			key: 'cost_head_name',
			render: (value) => value || '—',
		},
		{
			title: isCostPlus ? 'Cost to Company (₹)' : 'Value (₹)',
			dataIndex: 'value',
			key: 'value',
			width: 190,
			align: 'right',
			render: renderValueCell,
		},
		...(isCostPlus
			? [
					{
						title: 'Cost to Customer (₹)',
						dataIndex: 'cost_to_customer',
						key: 'cost_to_customer',
						width: 190,
						align: 'right',
						render: renderCostToCustomerCell,
					},
					{
						title: 'Generated Revenue',
						key: 'generated_revenue',
						align: 'right',
						render: renderGeneratedRevenueCell,
					},
			  ]
			: []),
		{
			title: 'Last Month',
			dataIndex: 'last_month_value',
			key: 'last_month_value',
			align: 'right',
			render: (value) => <NumCell>{money(value)}</NumCell>,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (value) =>
				value === MONTHLY_STATUS_PENDING ? (
					<StatusChip tone="warning" label="Pending" />
				) : (
					<StatusChip tone="success" label="Added" />
				),
		},
		...(showActionsColumn
			? [
					{
						title: '',
						key: 'actions',
						width: 150,
						align: 'center',
						render: renderActions,
					},
			  ]
			: []),
	];

	// One cell per column, in column order — built dynamically since the Cost Plus
	// columns above change how many there are.
	const renderSummary = () => {
		const cells = [
			<strong key="label">Total</strong>,
			<NumCell key="value">
				<strong>{money(enteredTotal)}</strong>
			</NumCell>,
			...(isCostPlus
				? [
						<NumCell key="ctcu">
							<strong>{money(enteredCostToCustomerTotal)}</strong>
						</NumCell>,
						<NumCell key="generated_revenue">
							<strong>{money(generatedRevenueTotal)}</strong>
						</NumCell>,
				  ]
				: []),
			<NumCell key="last_month">
				<strong>{money(lastMonthTotal)}</strong>
			</NumCell>,
			null, // Status
			...(showActionsColumn ? [null] : []),
		];
		return (
			<Table.Summary fixed>
				<Table.Summary.Row>
					{cells.map((content, index) => (
						<Table.Summary.Cell
							// eslint-disable-next-line react/no-array-index-key -- cells are positional, no stable id
							key={index}
							index={index}
							align={index === 0 ? 'left' : 'right'}
						>
							{content}
						</Table.Summary.Cell>
					))}
				</Table.Summary.Row>
			</Table.Summary>
		);
	};

	const renderMonthlyCosts = () => (
		<Panel>
			<PanelHeader>
				<Box>
					<PanelTitle>Month-End Costs — {periodMonth.format('MMMM YYYY')}</PanelTitle>
					<PanelSubtitle>
						Entered at month close. Revenue MTD: {money(figures?.revenue)}.
					</PanelSubtitle>
				</Box>
			</PanelHeader>
			{!loading && rows.length === 0 ? (
				<EmptyState>
					<div className="empty-title">No month-end costs yet</div>
					<div className="empty-body">
						Add the cost heads that apply to this site below.
					</div>
				</EmptyState>
			) : (
				<WideGutterTable
					rowKey="cost_head_id"
					columns={columns}
					dataSource={rows}
					loading={loading}
					pagination={false}
					scroll={{ x: isCostPlus ? 1180 : 820 }}
					// antd's summary API takes a render function by design
					// eslint-disable-next-line react/no-unstable-nested-components
					summary={renderSummary}
				/>
			)}

			{canAddMonthlyCostHead && (
				<Box mt="16px">
					<CostHeadAddRow custom={addingCustom ? 1 : 0}>
						<QuickAddField>
							<span className="field-label">Cost Head</span>
							<Select
								value={selectedHead}
								disabled={costFrozen}
								onChange={(v) => {
									setSelectedHead(v);
									if (v !== ADD_COST_HEAD_OPTION) setCustomHead('');
								}}
								placeholder="Select cost head"
								options={headOptions}
								showSearch
								optionFilterProp="label"
							/>
						</QuickAddField>

						{addingCustom && (
							<QuickAddField>
								<span className="field-label">New Cost Head</span>
								<Input
									placeholder="e.g. Water Charges"
									value={customHead}
									disabled={costFrozen}
									onChange={(e) => setCustomHead(e.target.value)}
									onPressEnter={handleAddAndSave}
								/>
							</QuickAddField>
						)}

						<QuickAddField>
							<span className="field-label">
								{isCostPlus ? 'Cost to Company (₹)' : 'Value (₹)'}
							</span>
							<Input
								type="number"
								min={0}
								placeholder="0"
								value={newValue}
								disabled={costFrozen}
								onChange={(e) => setNewValue(e.target.value)}
								onPressEnter={handleAddAndSave}
							/>
						</QuickAddField>

						{isCostPlus && (
							<QuickAddField>
								<span className="field-label">Cost to Customer (₹)</span>
								<Input
									type="number"
									min={0}
									placeholder="Same as Value"
									value={newCostToCustomer}
									disabled={costFrozen}
									onChange={(e) => setNewCostToCustomer(e.target.value)}
									onPressEnter={handleAddAndSave}
								/>
							</QuickAddField>
						)}

						<QuickAddField>
							<Tooltip title={costFrozenTooltip}>
								<Button
									type="primary"
									icon={<PlusOutlined />}
									loading={saving}
									disabled={costFrozen}
									onClick={handleAddAndSave}
								>
									Add &amp; Save
								</Button>
							</Tooltip>
						</QuickAddField>
					</CostHeadAddRow>

					<AddRowNote>
						{costFrozen
							? `Cost is frozen for ${monthLabel} — month-end costs are closed for that month.`
							: 'Saved values add to MTD (actual) immediately.'}
					</AddRowNote>
				</Box>
			)}
		</Panel>
	);

	const renderRevenueAdjustments = () => {
		const base = Number(figures?.base_revenue) || 0;
		const charges = Number(figures?.mtd_additional_charges) || 0;

		return (
			<Panel>
				<PanelHeader>
					<Box>
						<PanelTitle>Additional Revenue Heads (Provisional)</PanelTitle>
						<PanelSubtitle>
							Monthly open entries applied to overall revenue — additions or
							deductions.
						</PanelSubtitle>
					</Box>

					<PanelHeaderMetrics>
						<span className="metric-formula">
							Base {money(base)} + charges {money(charges)}
						</span>
						<MetricPill>MTD Revenue {money(figures?.revenue)}</MetricPill>
					</PanelHeaderMetrics>
				</PanelHeader>

				{revenueAdjustments.length === 0 ? (
					<EmptyState>
						<div className="empty-body">
							No revenue adjustments recorded this month.
						</div>
					</EmptyState>
				) : (
					<WideGutterTable
						rowKey={(record, index) => record.id ?? `adjustment-${index}`}
						size="small"
						columns={revenueAdjustmentColumns}
						dataSource={revenueAdjustments}
						pagination={false}
						scroll={{ x: 900 }}
					/>
				)}

				{canAddRevenueAdjustment && (
					<Box mt="16px">
						<CostHeadAddRow>
							<QuickAddField>
								<span className="field-label">Revenue Head</span>
								<Input
									placeholder="e.g. Peak Season Surcharge"
									value={adjustmentHead}
									disabled={revenueFrozen}
									onChange={(e) => setAdjustmentHead(e.target.value)}
									onPressEnter={handleAddAdjustment}
								/>
							</QuickAddField>

							<QuickAddField>
								<span className="field-label">Type</span>
								<Select
									value={adjustmentType}
									disabled={revenueFrozen}
									onChange={setAdjustmentType}
									options={ADJUSTMENT_TYPE_OPTIONS}
								/>
							</QuickAddField>

							<QuickAddField>
								<span className="field-label">Value (₹)</span>
								<Input
									type="number"
									min={0}
									placeholder="0"
									value={adjustmentValue}
									disabled={revenueFrozen}
									onChange={(e) => setAdjustmentValue(e.target.value)}
									onPressEnter={handleAddAdjustment}
								/>
							</QuickAddField>

							<QuickAddField>
								<Tooltip
									title={
										revenueFrozen
											? `Revenue is frozen for ${monthLabel} — no further entries`
											: undefined
									}
								>
									<Button
										type="primary"
										icon={<PlusOutlined />}
										loading={savingAdjustment}
										disabled={revenueFrozen}
										onClick={handleAddAdjustment}
									>
										Add &amp; Save
									</Button>
								</Tooltip>
							</QuickAddField>
						</CostHeadAddRow>

						<AddRowNote>
							{revenueFrozen
								? `Revenue is frozen for ${monthLabel} — revenue heads are closed for that month.`
								: 'Added as provisional revenue for this month, on top of the base revenue model.'}
						</AddRowNote>
					</Box>
				)}
			</Panel>
		);
	};

	return (
		<>
			{renderRevenueAdjustments()}
			<Box mt="24px">{renderMonthlyCosts()}</Box>

			<MonthlyCostEditModal
				open={Boolean(editTarget)}
				mode={editTarget?.mode}
				record={editTarget?.record}
				saving={savingEdit}
				isCostPlus={isCostPlus}
				onCancel={() => setEditTarget(null)}
				onSubmit={handleEditSubmit}
			/>
		</>
	);
}

MonthlyTab.propTypes = {
	projectId: PropTypes.number.isRequired,
	periodMonth: PropTypes.instanceOf(moment).isRequired,
	figures: PropTypes.object,
	loading: PropTypes.bool,
	onDataChanged: PropTypes.func.isRequired,
};

MonthlyTab.defaultProps = {
	figures: null,
	loading: false,
};
